import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import toast from 'components/Basic/Toast';
import { useLazyGetSegmentsReadinessQuery } from './MparticleService';
import { MparticleSegmentReadiness } from './types';

type UseSegmentsReadinessParams = {
  /**
   * Consumers decide when the data is worth fetching — for example, a page of segments with
   * no mparticle_audience row needs no readiness at all.
   */
  enabled: boolean;
};

/**
 * Readiness for every mparticle_audience segment, keyed by segment id.
 *
 * Shared by the segments list and the offer's segment dropdown, which is why it lives in the
 * mParticle domain rather than next to either consumer. It deliberately holds no global
 * state: each consumer owns its own copy, the same way the audience picker does.
 *
 * A failed request resolves to an empty map rather than an error state. Readiness is
 * supplementary information; losing it must not stop the segments list from rendering.
 */
const useSegmentsReadiness = ({ enabled }: UseSegmentsReadinessParams) => {
  const intl = useIntl();
  const [getSegmentsReadiness] = useLazyGetSegmentsReadinessQuery();

  const [readinessBySegmentId, setReadinessBySegmentId] = useState<Map<number, MparticleSegmentReadiness>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setReadinessBySegmentId(new Map());
      // Reset here too: a request in flight when `enabled` drops has its finally guarded by
      // `ignore`, so this branch is the only place left to clear the loading flag.
      setIsLoading(false);
      return undefined;
    }

    // Guards against a slow response resolving after `enabled` has toggled again and
    // overwriting the state a newer run already set.
    let ignore = false;

    const loadReadiness = async () => {
      try {
        setIsLoading(true);

        const response = await getSegmentsReadiness().unwrap();

        if (ignore) return;

        setReadinessBySegmentId(new Map((response?.segments || []).map(item => [item.segmentId, item])));
      } catch {
        if (ignore) return;

        // On failure every mParticle segment falls back to "synchronizing" and is treated as
        // not usable. That is deliberate — we never claim "ready" without confirmation — but the
        // fallback is indistinguishable from a genuine sync state, so surface the failure rather
        // than degrade silently, matching the audiences hook.
        setReadinessBySegmentId(new Map());

        // Fixed toastId dedupes: the page loads segments in two passes on mount (container +
        // table effects), so this hook can fetch — and fail — twice, but only one toast shows.
        toast.error(
          { title: intl.formatMessage({ id: 'Loading segments readiness is failure!' }) },
          { toastId: 'mparticle-readiness-error' },
        );
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    loadReadiness();

    return () => {
      ignore = true;
    };
  }, [enabled]);

  const findReadiness = (segmentId: number) => readinessBySegmentId.get(segmentId);

  return { readinessBySegmentId, findReadiness, isLoading };
};

export default useSegmentsReadiness;
